/** 标题模板作用域（个人 / 团队 / 官方） */
import i18n from '@/i18n';

export const TITLE_PRESET_SCOPES = [
  { key: 'personal', label: '个人' },
  { key: 'team', label: '团队' },
  { key: 'official', label: '官方' },
];

export function getTitlePresetScopeLabel(scopeKey) {
  switch (String(scopeKey || '').trim()) {
    case 'personal':
      return i18n.t('workbench:aiCwTemplatePicker.scopePersonal', { defaultValue: '个人' });
    case 'team':
      return i18n.t('workbench:aiCwTemplatePicker.scopeTeam', { defaultValue: '团队' });
    default:
      return i18n.t('workbench:aiCwTemplatePicker.scopeOfficial', { defaultValue: '官方' });
  }
}

export const DEFAULT_TITLE_PRESET_SCOPE = 'official';

/**
 * 根据当前工作台 mode 解析默认 Tab。
 * - team 模式且可访问团队 → team
 * - personal 模式 → personal
 * - 否则 → official
 */
export function resolveDefaultTitlePresetScope(workspaceMode, canAccessTeam = false) {
  const mode = String(workspaceMode || '').trim().toLowerCase();
  if (mode === 'team' && canAccessTeam) return 'team';
  if (mode === 'personal') return 'personal';
  return DEFAULT_TITLE_PRESET_SCOPE;
}

/** 规范化 scope；未知值回落 official（兼容旧数据缺字段） */
export function normalizeTitlePresetScope(scope) {
  const key = String(scope || '').trim().toLowerCase();
  if (key === 'personal' || key === 'team' || key === 'official') return key;
  return 'official';
}

/** 标题模板分类（与新增标题提取任务弹窗一致） */
export const TITLE_PRESET_CATEGORIES = [
  { key: 'all', label: '全部' },
  { key: 'clothing', label: '服饰', keywords: ['服', '短袖', '卫衣', '裤', '连衣裙', 'hoodie', 'shirt', 'dress', 'tee', 't恤'] },
  { key: 'home', label: '家居纺织', keywords: ['家纺', '抱枕', '地毯', '浴帘', '窗帘', '家居', 'pillow', 'rug', 'curtain'] },
  { key: 'ornament', label: '挂饰', keywords: ['挂饰', '挂件', 'ornament', 'pendant', '钟', '挂钟'] },
  { key: 'phone', label: '手机壳', keywords: ['手机壳', 'phone', 'iphone', 'case'] },
  { key: 'acrylic', label: '亚克力', keywords: ['亚克力', 'acrylic', '立牌', '杯垫'] },
  { key: 'other', label: '其他', keywords: [] },
];

/**
 * 内置官方标题模板。
 * 与 Flyway BE-05 种子（V20260714153000）内容对齐；
 * 仅在官方 list 接口失败时作前端兜底，接口成功则以库内数据为准。
 */
export const BUILTIN_TITLE_PRESETS = [
  {
    id: 'OFFICIAL-BUILTIN-TEE-01',
    name: '短袖T恤·潮流印花',
    category: 'clothing',
    content: '2D Flat Print 商品形状描述 印花图案内容 Graphic Tee for 对象 场景1 商品属性/风格 Gift',
  },
  {
    id: 'OFFICIAL-BUILTIN-TEE-02',
    name: '短袖T恤·休闲百搭',
    category: 'clothing',
    content: 'Casual 商品形状描述 印花图案内容 T-Shirt 男女同款 日常穿搭 舒适亲肤 夏季热销',
  },
  {
    id: 'OFFICIAL-BUILTIN-HOODIE-01',
    name: '卫衣·街头风',
    category: 'clothing',
    content: 'Unisex Hoodie 印花图案内容 Pullover 商品属性/风格 Streetwear for 对象 秋冬保暖',
  },
  {
    id: 'OFFICIAL-BUILTIN-DRESS-01',
    name: '连衣裙·甜美风',
    category: 'clothing',
    content: 'Women Dress 印花图案内容 商品属性/风格 优雅气质 约会出游 节日礼赠',
  },
  {
    id: 'OFFICIAL-BUILTIN-PANTS-01',
    name: '休闲裤·运动风',
    category: 'clothing',
    content: 'Jogger Pants 印花图案内容 弹力舒适 运动休闲 男女通用 潮流出街',
  },
  {
    id: 'OFFICIAL-BUILTIN-PILLOW-01',
    name: '抱枕套·家居装饰',
    category: 'home',
    content: 'Decorative Throw Pillow Cover 印花图案内容 商品属性/风格 Home Living Room Sofa Decor Gift',
  },
  {
    id: 'OFFICIAL-BUILTIN-RUG-01',
    name: '地毯·防滑款',
    category: 'home',
    content: 'Area Rug 印花图案内容 Non-Slip Floor Mat 客厅卧室 家居软装 易打理',
  },
  {
    id: 'OFFICIAL-BUILTIN-CURTAIN-01',
    name: '浴帘·防水款',
    category: 'home',
    content: 'Shower Curtain 印花图案内容 Waterproof Bathroom Decor 商品属性/风格 浴室收纳',
  },
  {
    id: 'OFFICIAL-BUILTIN-BLANKET-01',
    name: '毛毯·保暖款',
    category: 'home',
    content: 'Soft Blanket 印花图案内容 Cozy Throw 沙发午睡 秋冬保暖 送礼佳品',
  },
  {
    id: 'OFFICIAL-BUILTIN-ORNAMENT-01',
    name: '挂件·节日礼赠',
    category: 'ornament',
    content: 'Hanging Ornament 印花图案内容 Pendant Gift for 对象 节日 家居挂饰 精致包装',
  },
  {
    id: 'OFFICIAL-BUILTIN-CLOCK-01',
    name: '挂钟·简约风',
    category: 'ornament',
    content: 'Wall Clock 印花图案内容 Silent Quartz 客厅卧室 简约装饰 静音机芯',
  },
  {
    id: 'OFFICIAL-BUILTIN-PHONE-01',
    name: '手机壳·防摔款',
    category: 'phone',
    content: 'Phone Case 印花图案内容 Shockproof Protective Cover iPhone Samsung 商品属性/风格',
  },
  {
    id: 'OFFICIAL-BUILTIN-PHONE-02',
    name: '手机壳·可爱礼物',
    category: 'phone',
    content: 'Cute Phone Case 印花图案内容 Gift for 对象 防刮耐磨 轻薄手感 拍照出片',
  },
  {
    id: 'OFFICIAL-BUILTIN-ACRYLIC-01',
    name: '亚克力挂件',
    category: 'acrylic',
    content: 'Acrylic Ornament 印花图案内容 Pendant Keychain Gift for 对象 节日 精致通透',
  },
  {
    id: 'OFFICIAL-BUILTIN-ACRYLIC-02',
    name: '亚克力立牌',
    category: 'acrylic',
    content: 'Acrylic Standee 印花图案内容 Desk Decoration 桌面摆件 商品属性/风格 送礼优选',
  },
  {
    id: 'OFFICIAL-BUILTIN-ACRYLIC-03',
    name: '亚克力杯垫',
    category: 'acrylic',
    content: 'Acrylic Coaster 印花图案内容 Table Decor Set 隔热防滑 餐厅吧台 家居好物',
  },
  {
    id: 'OFFICIAL-BUILTIN-BAG-01',
    name: '帆布包·大容量',
    category: 'other',
    content: 'Canvas Tote Bag 印花图案内容 Reusable Shopping Bag 通勤上课 环保耐用 大容量',
  },
  {
    id: 'OFFICIAL-BUILTIN-MUG-01',
    name: '马克杯·礼品款',
    category: 'other',
    content: 'Ceramic Mug 印花图案内容 Coffee Cup Gift for 对象 节日 办公室家居 礼盒装',
  },
  {
    id: 'OFFICIAL-BUILTIN-STICKER-01',
    name: '贴纸·防水款',
    category: 'other',
    content: 'Vinyl Sticker 印花图案内容 Waterproof Decal 笔记本水杯 个性装饰 易撕不留胶',
  },
  {
    id: 'OFFICIAL-BUILTIN-GENERAL-01',
    name: '通用电商·爆款句式',
    category: 'other',
    content: '个性化定制 商品形状描述 印花图案内容 男女通用 休闲百搭 送礼优选 人气热销',
  },
];

export function inferTitlePresetCategory(preset) {
  if (preset?.category) return preset.category;
  const text = `${preset?.name || ''} ${preset?.content || ''}`.toLowerCase();
  for (const item of TITLE_PRESET_CATEGORIES) {
    if (item.key === 'all' || item.key === 'other') continue;
    if (item.keywords.some((kw) => text.includes(kw.toLowerCase()))) {
      return item.key;
    }
  }
  return 'other';
}

function normalizePresetRow(preset, fallbackScope = 'official', source = 'api') {
  if (!preset || typeof preset !== 'object') return null;
  const content = String(preset.content || '').trim();
  const name = String(preset.name || preset.title || '').trim() || '未命名模板';
  if (!content && !name) return null;
  const id = preset.id != null ? String(preset.id) : '';
  if (!id) return null;
  return {
    id,
    name,
    content: content || name,
    category: preset.category || inferTitlePresetCategory({ name, content }),
    scope: normalizeTitlePresetScope(preset.scope || preset.templateScope || fallbackScope),
    platform: preset.platform || '',
    forbiddenWords: Array.isArray(preset.forbiddenWords) ? preset.forbiddenWords : [],
    canEdit: Boolean(preset.canEdit),
    canDelete: Boolean(preset.canDelete),
    ownerUserId: preset.ownerUserId ?? null,
    source: preset.source || source,
  };
}

/**
 * 合并三 scope 真实数据源（personal / team / official）。
 * - 各条目保留自身 scope，不再强制归 official
 * - 官方：接口成功 → 以返回为准（可为空）；失败 → 内置库兜底
 * - 同 id 去重：后写入覆盖先写入（personal/team 优先于 builtin）
 */
export function mergeTitlePresets({
  personal = [],
  team = [],
  official = [],
  officialOk = false,
  // 兼容旧调用：扁平 apiPresets / officialTemplates
  apiPresets = null,
  officialTemplates = null,
} = {}) {
  const byId = new Map();

  const addAll = (list, fallbackScope, source) => {
    (list || []).forEach((raw) => {
      const row = normalizePresetRow(raw, fallbackScope, source);
      if (!row) return;
      byId.set(row.id, row);
    });
  };

  // 旧参：officialTemplates → official；apiPresets 按自身 scope 归桶（缺省 personal）
  if (Array.isArray(officialTemplates) && officialTemplates.length) {
    addAll(
      officialTemplates.map((p) => ({
        ...p,
        category: p.group || p.category,
      })),
      'official',
      'official',
    );
  }
  if (Array.isArray(apiPresets) && apiPresets.length) {
    apiPresets.forEach((p) => {
      const scope = normalizeTitlePresetScope(p?.scope || p?.templateScope || 'personal');
      const row = normalizePresetRow(p, scope, 'user');
      if (row) byId.set(row.id, row);
    });
  }

  addAll(personal, 'personal', 'api');
  addAll(team, 'team', 'api');

  if (officialOk) {
    addAll(official, 'official', 'api');
  } else if (!(Array.isArray(official) && official.length > 0)) {
    BUILTIN_TITLE_PRESETS.forEach((p) => {
      const row = normalizePresetRow({ ...p, scope: 'official' }, 'official', 'builtin');
      if (row && !byId.has(row.id)) byId.set(row.id, row);
    });
  } else {
    addAll(official, 'official', 'api');
  }

  return Array.from(byId.values());
}

export function filterTitlePresetsByScope(presets, scopeKey = DEFAULT_TITLE_PRESET_SCOPE) {
  const key = normalizeTitlePresetScope(scopeKey || DEFAULT_TITLE_PRESET_SCOPE);
  return (presets || []).filter((p) => normalizeTitlePresetScope(p.scope) === key);
}

/**
 * 顶栏工作区模式对应的作用域（也是弹窗内默认选中的 Tab / 新增模板的归属）。
 * - 团队模式且已开通/加入团队 → team
 * - 其余（个人模式、团队模式但无团队） → personal
 */
export function resolveLockedTitlePresetScope(workspaceMode, canAccessTeam = false) {
  const mode = String(workspaceMode || '').trim().toLowerCase();
  if (mode === 'team' && canAccessTeam) return 'team';
  return 'personal';
}

/** 锁定作用域展示名（弹窗只读标签用） */
export function getLockedTitlePresetScopeLabel(lockedScope) {
  return lockedScope === 'team' ? '团队' : '个人';
}

/**
 * 模板弹窗内可见的作用域 Tab：
 * - 个人模式：个人 + 官方
 * - 团队模式：团队 + 官方
 * 官方为公共模板，两种模式下都作为一个独立 Tab 提供（不与个人/团队数据混排）。
 */
export function resolveVisibleTitlePresetScopes(workspaceMode, canAccessTeam = false) {
  const primary = resolveLockedTitlePresetScope(workspaceMode, canAccessTeam);
  return TITLE_PRESET_SCOPES
    .filter((item) => item.key === primary || item.key === 'official')
    .map((item) => ({ ...item, label: getTitlePresetScopeLabel(item.key) }));
}

export function filterTitlePresetsByCategory(presets, categoryKey = 'all') {
  if (!categoryKey || categoryKey === 'all') return presets;
  return presets.filter((p) => (p.category || inferTitlePresetCategory(p)) === categoryKey);
}

export function getTitlePresetCategoryLabel(categoryKey) {
  const found = TITLE_PRESET_CATEGORIES.find((c) => c.key === categoryKey);
  const key = found?.key || 'other';
  return i18n.t(`workbench:titlePresetCategory.${key}`, { defaultValue: found?.label || '其他' });
}

export function filterTitlePresetsByKeyword(presets, keyword = '') {
  const q = String(keyword || '').trim().toLowerCase();
  if (!q) return presets;
  return presets.filter((p) => {
    const text = `${p.name || ''} ${p.content || ''}`.toLowerCase();
    return text.includes(q);
  });
}

/** 「全部」视图：按分类分组，顺序与 TITLE_PRESET_CATEGORIES 一致 */
export function groupTitlePresetsByCategory(presets) {
  const order = TITLE_PRESET_CATEGORIES.filter((c) => c.key !== 'all').map((c) => c.key);
  const byCategory = new Map();
  presets.forEach((preset) => {
    const key = preset.category || inferTitlePresetCategory(preset);
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key).push(preset);
  });

  const groups = [];
  order.forEach((key) => {
    const items = byCategory.get(key);
    if (items?.length) {
      groups.push({
        category: key,
        label: getTitlePresetCategoryLabel(key),
        presets: items,
      });
      byCategory.delete(key);
    }
  });
  byCategory.forEach((items, key) => {
    if (items.length) {
      groups.push({
        category: key,
        label: getTitlePresetCategoryLabel(key),
        presets: items,
      });
    }
  });
  return groups;
}
